import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import SuggestionForm from '../components/SuggestionForm';

/**
 * MentorKBPage — read-only Knowledge Base viewer for the mentor role.
 *
 * Fetches KB entries from `GET /api/mentors/kb`, groups them by category
 * in the defined display order, and shows an entry detail panel on selection.
 *
 * Error handling:
 *   - 403 → inactive link message (R4.5)
 *   - Network/5xx → error message + retry button (R4.6)
 *
 * No edit/delete/create controls are rendered — strictly read-only (R4.2).
 *
 * Requirements: 4.1–4.6
 */

/** Category display order per design */
const CATEGORY_ORDER = [
  'profile',
  'skills',
  'experience',
  'projects',
  'interests',
  'personal_statement',
] as const;

/** Human-readable category labels */
const CATEGORY_LABELS: Record<string, string> = {
  profile: 'Profile',
  skills: 'Skills',
  experience: 'Experience',
  projects: 'Projects',
  interests: 'Interests',
  personal_statement: 'Personal Statement',
};

export interface MentorKBEntry {
  sk: string;
  category: string;
  content: string;
  skills_extracted: string[];
  created_at: string;
}

type FetchState =
  | { status: 'loading' }
  | { status: 'success'; entries: MentorKBEntry[] }
  | { status: 'forbidden'; message: string }
  | { status: 'error'; message: string };

export default function MentorKBPage() {
  const [state, setState] = useState<FetchState>({ status: 'loading' });
  const [selectedEntry, setSelectedEntry] = useState<MentorKBEntry | null>(null);

  const fetchEntries = useCallback(async () => {
    setState({ status: 'loading' });
    setSelectedEntry(null);
    try {
      const data = await api.get<MentorKBEntry[]>('/api/mentors/kb');
      setState({ status: 'success', entries: data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setState({
          status: 'forbidden',
          message: 'Your mentor link is not active or KB access is not included in your granted scope.',
        });
      } else {
        setState({
          status: 'error',
          message: 'Knowledge Base entries could not be loaded.',
        });
      }
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // R4.1: Loading indicator
  if (state.status === 'loading') {
    return (
      <div aria-label="Loading knowledge base">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Knowledge Base</h1>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-48" />
          <div className="h-4 bg-gray-200 rounded w-full" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-6 bg-gray-200 rounded w-48 mt-6" />
          <div className="h-4 bg-gray-200 rounded w-full" />
          <div className="h-4 bg-gray-200 rounded w-5/6" />
        </div>
      </div>
    );
  }

  // R4.5: 403 → inactive link message
  if (state.status === 'forbidden') {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Knowledge Base</h1>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" role="alert">
          <p className="text-amber-800">{state.message}</p>
        </div>
      </div>
    );
  }

  // R4.6: Network/5xx → error + retry
  if (state.status === 'error') {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Knowledge Base</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
          <p className="text-red-700">{state.message}</p>
          <button
            onClick={fetchEntries}
            className="mt-3 text-sm font-medium text-red-700 hover:text-red-900 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // R4.1: Group entries by category in defined order
  const grouped = groupByCategory(state.entries);

  // Empty state — no entries returned
  if (state.entries.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Knowledge Base</h1>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" role="status">
          <p className="text-gray-600">No Knowledge Base entries available.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Knowledge Base</h1>

      {/* Two-panel layout: entry list (left) + detail panel (right) */}
      <div className="flex gap-6">
        {/* Left panel — entry list grouped by category */}
        <div className="w-1/2 space-y-6">
          {CATEGORY_ORDER.map((category) => {
            const entries = grouped.get(category);
            if (!entries || entries.length === 0) return null;
            return (
              <section key={category} aria-labelledby={`category-${category}`}>
                <h2
                  id={`category-${category}`}
                  className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {CATEGORY_LABELS[category] ?? category}
                </h2>
                <ul className="space-y-2">
                  {entries.map((entry) => (
                    <li key={entry.sk}>
                      <button
                        onClick={() => setSelectedEntry(entry)}
                        className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                          selectedEntry?.sk === entry.sk
                            ? 'border-blue-300 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        aria-pressed={selectedEntry?.sk === entry.sk}
                      >
                        <p className="text-sm text-gray-900 line-clamp-2">
                          {entry.content.slice(0, 120)}
                          {entry.content.length > 120 ? '…' : ''}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(entry.created_at)}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>

        {/* Right panel — entry detail + suggestion form (R4.3) */}
        <div className="w-1/2 space-y-4">
          {selectedEntry ? (
            <EntryDetail entry={selectedEntry} />
          ) : (
            <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500">
              <p>Select an entry to view its details.</p>
            </div>
          )}

          {/* Suggestion form — always visible (R4.3), auto-populates target_ref from selected entry */}
          <SuggestionForm targetRef={selectedEntry?.sk ?? null} />
        </div>
      </div>
    </div>
  );
}

/**
 * Entry detail panel — displays category, content, skills_extracted, created_at.
 * Strictly read-only; no edit/delete controls (R4.2).
 */
function EntryDetail({ entry }: { entry: MentorKBEntry }) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white p-5 sticky top-6">
      {/* Category badge */}
      <span className="inline-block text-xs font-medium bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full mb-3">
        {CATEGORY_LABELS[entry.category] ?? entry.category}
      </span>

      {/* Content */}
      <div className="prose prose-sm max-w-none text-gray-800 mb-4 whitespace-pre-wrap">
        {entry.content}
      </div>

      {/* Skills extracted */}
      {entry.skills_extracted.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Skills Extracted
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {entry.skills_extracted.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Created date */}
      <p className="text-xs text-gray-500">
        Created: {formatDate(entry.created_at)}
      </p>
    </div>
  );
}

/**
 * Groups entries by category, maintaining the CATEGORY_ORDER.
 * Entries not matching a known category are placed at the end.
 */
function groupByCategory(entries: MentorKBEntry[]): Map<string, MentorKBEntry[]> {
  const grouped = new Map<string, MentorKBEntry[]>();

  // Initialize with known categories in order
  for (const cat of CATEGORY_ORDER) {
    grouped.set(cat, []);
  }

  for (const entry of entries) {
    const list = grouped.get(entry.category);
    if (list) {
      list.push(entry);
    } else {
      // Unknown category — add as new group at the end
      const existing = grouped.get(entry.category) ?? [];
      existing.push(entry);
      grouped.set(entry.category, existing);
    }
  }

  return grouped;
}

/** Format ISO date string to a readable locale date-time */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
